import React, { useEffect, useContext, useState } from "react";
import {
  GridContainer,
  GridItem,
  FormRootArea,
} from "./MerchantPeriodicReport.styles";
import {
  merchantPeriodicInitialValues,
  merchantPeriodicValidationSchema,
} from "./MerchantPeriodicReport.resources";
import { useFormik } from "formik";
import TextField from "../../textField/TextField";
import DateField from "../../dateField/DateField";
import { Button } from "@mui/material";
import { useDispatch } from "react-redux";
import { generateReport } from "./MerchantPeriodicReport.thunk";
import AuthContext from "../../../context/AuthContext";
import CurrencySelectField from "../../selectField/CurrencySelectField";
import MerchantPeriodicReportResultModal from "./MerchantPeriodicReport.resultModal";
import moment from "moment";
import { useModal } from "../../modal";
import { useSelector } from "react-redux";
import AdminFields from "../../adminFields/AdminFields";
import {
  setApplyChanges,
  setWasUsed,
  setReset,
} from "../../adminFields/AdminFields.slice";
import { InputAdornment, IconButton } from "@mui/material";
import ListIcon from "@mui/icons-material/List";
import AdminFieldsModal from "../../adminFields/AdminFields.modal";
import LoadingBackdrop from "../../LoadingBackdrop";

const MerchantPeriodicReport = () => {
  const dispatch = useDispatch();
  const {
    token,
    acquirerData,
    userInfo: { "acquirer-rol-data": userRole },
  } = useContext(AuthContext);
  const { merchantID: acquirerMerchantID, currency_code: currency, isoID } =
    acquirerData || {};
  const { open } = useModal();
  const { status, retryCount } = useSelector(
    (state) => state.merchantPeriodicReport,
  );
  const {
    applyChanges,
    acquirerId,
    isoID: storeIsoID,
    wasUsed,
    reset,
    acquirerCurrencyId,
  } = useSelector((state) => state.adminFields);
  const [modalType, setModalType] = useState("merchant");
  const [modalOpen, setModalOpen] = useState(false);

  const handleOpenResultModal = () => {
    open({
      title: `Merchant date range Report from ${moment(
        values.reportStartDate,
      ).format("MM/DD/YYYY")} to ${moment(values.reportEndDate).format(
        "MM/DD/YYYY",
      )}`,
      maxWidth: "lg",
      fullWidth: true,
    });
  };

  const handleOpenModalMerchant = () => {
    setModalType("merchant");
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setModalType(null);
  };

  const handleSelectMerchant = (selectedMerchant) => {
    setFieldValue("merchantID", selectedMerchant.merchantID);
  };

  const {
    values,
    handleSubmit,
    isSubmitting,
    handleChange,
    handleBlur,
    errors,
    touched,
    setSubmitting,
    setFieldValue,
    setFieldTouched,
    validateForm,
  } = useFormik({
    initialValues: merchantPeriodicInitialValues,
    validationSchema: merchantPeriodicValidationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: false,
    onSubmit: (vals) => {
      setSubmitting(true);
      dispatch(
        generateReport({
          merchantID: vals.merchantID,
          holdingID: "",
          groupID: "",
          isoID: wasUsed ? storeIsoID : isoID,
          currency: vals.currency,
          reportStartDate: vals.reportStartDate,
          reportEndDate: vals.reportEndDate,
          token,
          acquirerData,
          onSuccessCallback: handleOpenResultModal,
          ...(wasUsed && { storeAcquirerId: acquirerId }),
        }),
      );
      setSubmitting(false);
    },
  });

  useEffect(() => {
    if (reset) {
      setFieldValue("merchantID", acquirerMerchantID || "");
      setFieldValue("currency", currency || "");
      dispatch(setReset(false));
    }
  }, [reset, acquirerMerchantID, currency, setFieldValue, dispatch]);

  useEffect(() => {
    if (applyChanges) {
      setFieldValue("currency", acquirerCurrencyId);
      dispatch(setApplyChanges(false));
      dispatch(setWasUsed(true));
    }
  }, [applyChanges, acquirerCurrencyId, setFieldValue, dispatch]);

  useEffect(() => {
    if (wasUsed && storeIsoID === "" && acquirerId === "") {
      dispatch(setApplyChanges(false));
      dispatch(setWasUsed(false));
      setFieldValue("merchantID", acquirerMerchantID || "");
      setFieldValue("currency", currency || "");
    }
  }, [
    storeIsoID,
    acquirerId,
    wasUsed,
    acquirerMerchantID,
    currency,
    setFieldValue,
    dispatch,
  ]);

  useEffect(() => {
    if (acquirerMerchantID && values.merchantID === "") {
      setFieldValue("merchantID", acquirerMerchantID);
    }
    if (currency && !wasUsed && (values.currency === "" || values.currency !== currency)) {
      setFieldValue("currency", currency);
    }
  }, [acquirerMerchantID, currency, wasUsed, values.merchantID, values.currency, setFieldValue]);

  useEffect(() => {
    if (values.reportStartDate || values.reportEndDate) {
      validateForm();
    }
  }, [values.reportStartDate, values.reportEndDate, validateForm]);

  return (
    <FormRootArea>
      <LoadingBackdrop
        loading={status === "loading"}
        retryCount={retryCount}
      />
      <AdminFields />
      <GridContainer component="form" onSubmit={handleSubmit}>
        <MerchantPeriodicReportResultModal />
        {modalOpen && modalType && (
          <AdminFieldsModal
            type={modalType}
            open={modalOpen}
            onClose={handleCloseModal}
            onSelectMerchant={handleSelectMerchant}
          />
        )}
        <GridItem xs={12} sm={6} lg={3} padding="15px 15px 15px 0">
          <TextField
            id="merchantID"
            name="merchantID"
            fullWidth
            disabled={["merchant", "merchant-iso"].includes(userRole)}
            label="Merchant ID"
            value={values.merchantID}
            onChange={handleChange}
            onBlur={handleBlur}
            required
            error={errors.merchantID}
            touched={touched.merchantID}
            InputProps={{
              endAdornment: ["acquirer", "acquirer-iso", "ISO", "admin"].includes(
                userRole,
              ) && (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="open Merchant list"
                    onClick={handleOpenModalMerchant}
                  >
                    <ListIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </GridItem>
        <GridItem xs={12} sm={6} lg={3} padding="15px 15px 15px 0">
          <CurrencySelectField
            id="currency"
            name="currency"
            fullWidth
            label="Currency"
            value={values.currency}
            required
            maxLength={3}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.currency}
            touched={touched.currency}
            disabled
          />
        </GridItem>
        <GridItem xs={12} sm={6} lg={3} padding="15px 15px 15px 0">
          <DateField
            id="reportStartDate"
            name="reportStartDate"
            fullWidth
            label="Report Start Date"
            showTimezoneLabel
            value={values.reportStartDate}
            onChange={handleChange}
            onBlur={handleBlur}
            setFieldTouched={setFieldTouched}
            required
            error={errors.reportStartDate}
            touched={touched.reportStartDate}
            minDate={moment().subtract(90, "days")}
            maxDate={moment().subtract(1, "day")}
            helperText="Select a date within the last 90 days"
          />
        </GridItem>
        <GridItem xs={12} sm={6} lg={3} padding="15px 15px 15px 0">
          <DateField
            id="reportEndDate"
            name="reportEndDate"
            fullWidth
            label="Report End Date"
            showTimezoneLabel
            value={values.reportEndDate}
            onChange={handleChange}
            onBlur={handleBlur}
            setFieldTouched={setFieldTouched}
            required
            error={errors.reportEndDate}
            touched={touched.reportEndDate}
            minDate={moment().subtract(90, "days")}
            maxDate={moment().subtract(1, "day")}
            helperText="Select a date within the last 90 days"
          />
        </GridItem>
        <GridContainer>
          <GridItem xs={12} sm={6} lg={4} padding="15px 15px 15px 0">
            <Button
              variant="contained"
              type="submit"
              size="large"
              disabled={isSubmitting || status === "loading"}
              loading={isSubmitting || status === "loading"}
              onClick={handleSubmit}
            >
              Generate
            </Button>
          </GridItem>
        </GridContainer>
      </GridContainer>
    </FormRootArea>
  );
};

export default MerchantPeriodicReport;
